// src/services/package.service.ts
// Single responsibility: all HTTP calls for packages.

import axios, { AxiosError, AxiosInstance } from 'axios';
import { AppConfig } from '../config/app.config';
import { authService } from './auth.service';
import {
  PackageResponse, CreatePackageCommand, PackageStatus,
  AddLocationCommand, PackageSearchCriteria, PagedResponse, ApiResponse,
} from '../types/package.types';

const createApiClient = (): AxiosInstance => {
  const client = axios.create({
    baseURL: AppConfig.api.baseUrl,
    headers: { 'Content-Type': 'application/json' },
    timeout: 15_000,
  });

  // Attach JWT on every request
  client.interceptors.request.use(config => {
    const token = authService.getToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  });

  // Global error handler
  client.interceptors.response.use(
    res => res,
    (error: AxiosError) => {
      if (error.response?.status === 401) {
        authService.clearSession();
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }
  );

  return client;
};

const api = createApiClient();
const BASE = `/api/${AppConfig.api.version}/packages`;

const parseError = (error: AxiosError): string => {
  if (!error.response) return 'No se pudo conectar al servidor.';
  const { status, data } = error.response as { status: number; data: any };
  if (status === 403) return 'No tienes permisos para realizar esta acción.';
  if (status === 404) return 'Paquete no encontrado.';
  if (status === 400) return data?.message || 'Datos inválidos.';
  if (status >= 500) return 'Error del servidor. Inténtalo más tarde.';
  return data?.message || 'Error inesperado.';
};

export const packageService = {
  create: async (command: CreatePackageCommand): Promise<PackageResponse> => {
    try {
      const { data } = await api.post<ApiResponse<PackageResponse>>(BASE, command);
      return data.data;
    } catch (e) { throw new Error(parseError(e as AxiosError)); }
  },

  getById: async (packageId: string): Promise<PackageResponse> => {
    try {
      const { data } = await api.get<ApiResponse<PackageResponse>>(`${BASE}/${packageId}`);
      return data.data;
    } catch (e) { throw new Error(parseError(e as AxiosError)); }
  },

  updateStatus: async (packageId: string, status: PackageStatus): Promise<PackageResponse> => {
    try {
      const { data } = await api.put<ApiResponse<PackageResponse>>(
        `${BASE}/${packageId}/status`, null, { params: { status } }
      );
      return data.data;
    } catch (e) { throw new Error(parseError(e as AxiosError)); }
  },

  addLocation: async (packageId: string, command: AddLocationCommand): Promise<PackageResponse> => {
    try {
      const { data } = await api.post<ApiResponse<PackageResponse>>(
        `${BASE}/${packageId}/locations`, { ...command, packageId }
      );
      return data.data;
    } catch (e) { throw new Error(parseError(e as AxiosError)); }
  },

  search: async (
    criteria: PackageSearchCriteria,
    page = 0,
    size = 20
  ): Promise<PagedResponse<PackageResponse>> => {
    try {
      const { data } = await api.get<ApiResponse<PagedResponse<PackageResponse>>>(BASE, {
        params: { ...criteria, page, size, sort: 'createdAt,desc' },
      });
      return data.data;
    } catch (e) { throw new Error(parseError(e as AxiosError)); }
  },

  getByStatus: async (status: PackageStatus): Promise<PackageResponse[]> => {
    try {
      const { data } = await api.get<ApiResponse<PackageResponse[]>>(`${BASE}/status/${status}`);
      return data.data;
    } catch (e) { throw new Error(parseError(e as AxiosError)); }
  },

  deletePackage: async (packageId: string): Promise<void> => {
    try {
      await api.delete(`${BASE}/${packageId}`);
    } catch (e) { throw new Error(parseError(e as AxiosError)); }
  },
};
